import { useEffect } from 'react';

interface SeoHelmetProps {
  title: string;
  description: string;
  keywords: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>[];
}

export default function SeoHelmet({
  title,
  description,
  keywords,
  canonicalPath = '/',
  ogType = 'website',
  ogImage,
  schema,
}: SeoHelmetProps) {
  useEffect(() => {
    const baseUrl = `${window.location.origin}${__BASE_PATH__}`;
    const canonical = `${baseUrl}${canonicalPath}`.replace(/\/$/, '') || baseUrl;

    document.title = title;

    const setMeta = (name: string, content: string, propName = 'name') => {
      let el = document.querySelector(`meta[${propName}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(propName, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', ogType, 'property');
    setMeta('og:url', canonical, 'property');
    setMeta('og:site_name', '丫丫漫画', 'property');
    setMeta('og:locale', 'zh_CN', 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    if (ogImage) {
      setMeta('og:image', ogImage, 'property');
      setMeta('twitter:image', ogImage);
    }

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);

    if (schema && schema.length > 0) {
      const existingScripts = document.querySelectorAll('script[data-seo-schema]');
      existingScripts.forEach((s) => s.remove());

      schema.forEach((s) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-schema', 'true');
        script.textContent = JSON.stringify(s);
        document.head.appendChild(script);
      });
    }

    return () => {
      const scripts = document.querySelectorAll('script[data-seo-schema]');
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonicalPath, ogType, ogImage, schema]);

  return null;
}